import { useEffect, useState, useRef } from "react";
import { Upload, Image as ImgIcon, X, Loader2, AlertCircle } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";

const ACCEPT = "*/*";   // all file types allowed (only images preview in the Excel quote)
const MAX_MB = 2;

/**
 * R29 Phase B — drawing screenshot uploader (JPG/PNG, ≤2 MB).
 * Scoped by `scope` (= "npd" | "assembly"). The screenshot is what gets
 * embedded into the customer-facing Excel/PDF quote, so it's mandatory.
 *
 * `path` is the storage path read from the parent entity. When non-empty
 * the inline preview is rendered using ?auth=<token> on the URL.
 */
export default function ScreenshotUpload({ scope, entityId, path, uploadedAt, sizeBytes, canEdit, onChanged }) {
  const [busy, setBusy] = useState(false);
  const [drag, setDrag] = useState(false);
  const [previewUrl, setPreviewUrl] = useState(null);
  const fileRef = useRef(null);

  useEffect(() => {
    let revoke;
    if (path) {
      const url = scope === "assembly"
        ? `/assemblies/${entityId}/screenshot`
        : `/npd/${entityId}/screenshot`;
      api
        .get(url, { responseType: "blob" })
        .then((r) => {
          const blobUrl = URL.createObjectURL(r.data);
          revoke = blobUrl;
          setPreviewUrl(blobUrl);
        })
        .catch(() => setPreviewUrl(null));
    } else {
      setPreviewUrl(null);
    }
    return () => { if (revoke) URL.revokeObjectURL(revoke); };
  }, [entityId, path, scope]);

  const uploadFile = async (file) => {
    if (!file) return;
    if (file.size > MAX_MB * 1024 * 1024) {
      toast.error(`Screenshot too large (max ${MAX_MB} MB)`);
      return;
    }
    const fd = new FormData();
    fd.append("file", file);
    setBusy(true);
    try {
      const url = scope === "assembly"
        ? `/assemblies/${entityId}/screenshot`
        : `/npd/${entityId}/screenshot`;
      const r = await api.post(url, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Screenshot uploaded");
      onChanged && onChanged(r.data);
    } catch (e) {
      // Surface 402 quota-exceeded with an admin-friendly message (R30 backend
      // distinguishes it from 500). Anything else falls back to detail.
      if (e?.response?.status === 402) {
        toast.error("Object storage credits exhausted — please ask the admin to top up.");
      } else {
        toast.error(e?.response?.data?.detail || "Upload failed");
      }
    } finally {
      setBusy(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };
  const onUpload = (ev) => uploadFile(ev.target.files?.[0]);
  const dragProps = {
    onDragOver: (e) => { e.preventDefault(); if (!drag) setDrag(true); },
    onDragLeave: (e) => { e.preventDefault(); setDrag(false); },
    onDrop: (e) => { e.preventDefault(); setDrag(false); if (canEdit && !busy) uploadFile(e.dataTransfer?.files?.[0]); },
  };

  const onDelete = async () => {
    if (!window.confirm("Remove screenshot?")) return;
    try {
      const url = scope === "assembly"
        ? `/assemblies/${entityId}/screenshot`
        : `/npd/${entityId}/screenshot`;
      await api.delete(url);
      toast.success("Screenshot removed");
      onChanged && onChanged(null);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed");
    }
  };

  const has = !!path;

  return (
    <div className="col-span-2" data-testid="screenshot-upload">
      {has ? (
        <div className="p-4 border border-slate-200 rounded-md bg-slate-50">
          <div className="flex items-start gap-3">
            <div className="w-20 h-16 bg-white border border-slate-200 rounded-md overflow-hidden flex-shrink-0 flex items-center justify-center">
              {previewUrl ? (
                <img src={previewUrl} alt="Screenshot preview" className="w-full h-full object-contain" />
              ) : (
                <ImgIcon size={24} className="text-slate-300" />
              )}
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-sm font-medium text-slate-900">Screenshot uploaded</div>
              <div className="text-xs text-slate-500">
                {sizeBytes ? `${(sizeBytes / 1024).toFixed(1)} KB` : "—"}
                {uploadedAt && <> · uploaded {new Date(uploadedAt).toLocaleDateString("en-IN")}</>}
              </div>
            </div>
          </div>
          <div className="mt-3 pt-2 border-t border-slate-200 flex flex-wrap items-center gap-3">
            {canEdit && (
              <button type="button" onClick={onDelete} className="text-xs text-red-600 hover:text-red-700 flex items-center gap-1" data-testid="screenshot-remove">
                <X size={12} /> Remove
              </button>
            )}
            {canEdit && (
              <label className="text-xs text-slate-600 hover:text-slate-900 cursor-pointer flex items-center gap-1">
                <Upload size={12} /> Replace
                <input ref={fileRef} type="file" accept={ACCEPT} className="hidden" onChange={onUpload} disabled={busy} />
              </label>
            )}
          </div>
        </div>
      ) : canEdit ? (
        <label
          {...dragProps}
          className={`border-2 border-dashed rounded-md p-6 flex flex-col items-center justify-center text-center cursor-pointer transition-colors ${drag ? "border-amber-600 bg-amber-100" : "border-amber-400 hover:border-amber-500 bg-amber-50/40 hover:bg-amber-50"}`}
          data-testid="screenshot-upload-zone"
        >
          {busy ? (
            <><Loader2 size={28} className="text-amber-500 animate-spin mb-2" /><span className="text-sm text-slate-600">Uploading…</span></>
          ) : (
            <>
              <Upload size={28} className="text-amber-500 mb-2" />
              <span className="text-sm font-medium text-slate-700">{drag ? "Drop file to upload" : "Click or drag & drop screenshot"}</span>
              <span className="text-xs text-slate-500 mt-1">Any file type · max {MAX_MB} MB · (image previews in the Excel quote)</span>
            </>
          )}
          <input ref={fileRef} type="file" accept={ACCEPT} className="hidden" onChange={onUpload} disabled={busy} />
        </label>
      ) : (
        <div className="border border-slate-200 rounded-md p-4 text-center text-sm text-slate-400 bg-slate-50 flex items-center justify-center gap-2">
          <AlertCircle size={14} /> No screenshot uploaded
        </div>
      )}
    </div>
  );
}
